/* Deliverables derivation (W3-G) — the ONE real deliverables source available
 * today: this client's archived research-run results.
 *
 * DISCOVERY (backend, read-only): the anatomy's Deliverables registry (§8) is an
 * internal registry of EXTERNALLY created Drive files and artifacts. The service
 * carries an `external_artifacts` table (tools/wsdb.py) for exactly that, but it
 * is Phase 0 — no Drive/Jira/meeting adapter populates it and no endpoint LISTS
 * its rows (only per-source COUNTS via GET /clients/{slug}/overview). So there is
 * no real external-deliverable data to render yet.
 *
 * What IS real and derivable frontend-side: the run archives (GET /runs → local[]),
 * each a dated, downloadable JSON result of a research run for this client. These
 * are genuine generated artifacts — the cockpit already lists them for the
 * refresh-history dropdown. This module turns them into honest deliverable
 * records (date · downloadable link · size), keeping the single run-name parser
 * the cockpit uses so there is one source of truth for the `{slug}-{date}-{time}-
 * result.json` grammar.
 */

/** Parsed parts of an archived run filename, or null when it doesn't match the
 * canonical `{slug}-YYYYMMDD-HHMMSS-result.json` grammar. */
export interface ParsedRunName {
  slug: string;
  /** YYYY-MM-DD */
  date: string;
  /** HH:MM */
  time: string;
  /** "YYYY-MM-DD HH:MM" */
  label: string;
}

/** Pure: an archive filename → its parts. Non-greedy slug capture so a hyphenated
 * slug (`el-al`) is preserved and only the trailing `-YYYYMMDD-HHMMSS-result.json`
 * is split off. */
export function parseRunName(name: string): ParsedRunName | null {
  const m = name.match(/^(.+?)-(\d{8})-(\d{6})-result\.json$/);
  if (!m) return null;
  const [, slug = '', d = '', tm = ''] = m;
  const date = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  const time = `${tm.slice(0, 2)}:${tm.slice(2, 4)}`;
  return { slug, date, time, label: `${date} ${time}` };
}

/** A downloadable research-run artifact for one client. */
export interface RunArtifact {
  name: string;
  /** YYYY-MM-DD */
  date: string;
  /** "YYYY-MM-DD HH:MM" */
  label: string;
  /** the served download path (`/test-runs/{name}`). */
  href: string;
  /** rounded KB, or null when the listing carried no size. */
  sizeKb: number | null;
}

/** Pure: the run listing (GET /runs → local[]) filtered to ONE client and mapped
 * to downloadable artifacts, newest first. A run whose filename doesn't parse, or
 * whose slug doesn't match, is dropped — never guessed into this client's book. */
export function clientRunArtifacts(
  slug: string,
  runs: readonly { name?: string; size?: number }[],
): RunArtifact[] {
  const out: RunArtifact[] = [];
  for (const r of runs) {
    const name = r.name || '';
    const p = parseRunName(name);
    if (!p || p.slug !== slug) continue;
    const size = typeof r.size === 'number' && Number.isFinite(r.size) && r.size >= 0 ? r.size : null;
    out.push({
      name,
      date: p.date,
      label: p.label,
      href: `/test-runs/${name}`,
      sizeKb: size === null ? null : Math.max(1, Math.round(size / 1024)),
    });
  }
  // Newest first — the filename's YYYYMMDD-HHMMSS sorts lexicographically.
  out.sort((a, b) => b.name.localeCompare(a.name));
  return out;
}
